import { z, type ZodType } from './schema'
import { tableSqlRef } from './naming'
import { safe } from '@w/utils'
import { Condition } from './condition'
import { maskColumnValue } from './redaction'
import type {
  ColumnMetadata,
  Dialect,
  InsertionType,
  NumericExpression,
  ParamWithMeta,
  SecurityCheckContext,
  SqlExpression,
  SqlFragment,
  sqlExpressionResultType,
} from './types'

/**
 * Lazy reference type for foreign keys - resolved during table binding
 */
// oxlint-disable-next-line typescript/no-explicit-any
type LazyReference = {
  // oxlint-disable-next-line typescript/no-explicit-any
  ref: () => Column<any, any, any>
  onDelete?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
  onUpdate?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
}

type ValueSql = (value: unknown) => SqlFragment
type NumericValue = number | bigint
type NumericOperand<T> = T extends NumericValue ? T | SqlExpression<T> : never
type NumericResult<T> = T extends NumericValue ? NumericExpression<T> : never

class NumericSqlExpression<
  T extends NumericValue,
> implements NumericExpression<T> {
  declare readonly [sqlExpressionResultType]: (value: T) => T

  constructor(
    private readonly left: SqlFragment,
    private readonly operator: '+' | '-' | '*' | '/' | '%',
    private readonly right: T | SqlExpression<T>,
    private readonly renderValue: (
      value: T,
      paramOffset: number,
      dialect: Dialect,
    ) => { sql: string; params: ParamWithMeta[] },
  ) {}

  add(value: T | SqlExpression<T>): NumericExpression<T> {
    return this._next('+', value)
  }

  sub(value: T | SqlExpression<T>): NumericExpression<T> {
    return this._next('-', value)
  }

  mul(value: T | SqlExpression<T>): NumericExpression<T> {
    return this._next('*', value)
  }

  div(value: T | SqlExpression<T>): NumericExpression<T> {
    return this._next('/', value)
  }

  mod(value: T | SqlExpression<T>): NumericExpression<T> {
    return this._next('%', value)
  }

  toSQL(
    paramOffset: number,
    dialect: Dialect = 'postgres',
  ): { sql: string; params: ParamWithMeta[] } {
    const left = this.left.toSQL(paramOffset, dialect)
    const rightOffset = paramOffset + left.params.length
    const right = isSqlFragment(this.right)
      ? this.right.toSQL(rightOffset, dialect)
      : this.renderValue(this.right, rightOffset, dialect)

    return {
      sql: `(${left.sql} ${this.operator} ${right.sql})`,
      params: [...left.params, ...right.params],
    }
  }

  private _next(
    operator: '+' | '-' | '*' | '/' | '%',
    right: T | SqlExpression<T>,
  ): NumericExpression<T> {
    return new NumericSqlExpression(this, operator, right, this.renderValue)
  }
}

declare const columnNameBrand: unique symbol
export type ColumnName = string & { readonly [columnNameBrand]: true }

declare const generatedColumnBrand: unique symbol
export type GeneratedColumnMarker = {
  readonly [generatedColumnBrand]: true
}

/**
 * Interface for any object that can be used as a column reference (has qualifiedName)
 */
/** T is used for type-safety in filter operators (eq, ne, gt, etc.) */
// oxlint-disable-next-line @typescript-eslint/no-unused-vars
export interface ColumnRef<T = unknown> {
  qualifiedName: string
  toSQL?: SqlFragment['toSQL']
}

/**
 * Type guard for column references (Column or AliasedColumn)
 */
function isColumnRef(value: unknown): value is ColumnRef {
  return (
    value !== null &&
    typeof value === 'object' &&
    'qualifiedName' in value &&
    typeof value.qualifiedName === 'string'
  )
}

function renderColumnRef(value: ColumnRef, dialect: Dialect): string {
  return value.toSQL?.(1, dialect).sql ?? value.qualifiedName
}

/**
 * Type guard distinguishing a SqlFragment from app values like Date/Uint8Array.
 */
function isSqlFragment(value: unknown): value is SqlFragment {
  return (
    value !== null &&
    typeof value === 'object' &&
    'toSQL' in value &&
    typeof value.toSQL === 'function'
  )
}

/**
 * Serializes an encoded primitive into a SQL literal for use in DDL DEFAULT.
 * Supports strings, numbers, booleans, null, bigint, and Uint8Array (as X'..').
 */
function serializeDefaultLiteral(value: unknown): string {
  if (value === null) return 'NULL'
  if (typeof value === 'string') return `'${value.replaceAll("'", "''")}'`
  if (typeof value === 'number' || typeof value === 'bigint')
    return String(value)
  if (typeof value === 'boolean') return String(value)
  if (value instanceof Uint8Array) {
    let hex = ''
    for (const b of value) hex += b.toString(16).padStart(2, '0')
    return `X'${hex}'`
  }
  throw new TypeError(
    `Unsupported default value (after encoding): ${typeof value} ${JSON.stringify(value)}`,
  )
}

function formatRejectedValue(value: unknown, columnName: string): string {
  const masked = maskColumnValue(value, columnName)
  if (masked === undefined) return 'undefined'
  if (typeof masked === 'bigint') return `${masked}n`
  try {
    return JSON.stringify(masked)
  } catch {
    return '[unserializable]'
  }
}

/**
 * Represents a database column with type information and filter operators.
 * Used by both PostgreSQL and SQLite dialects.
 *
 * @template T - The TypeScript type of the column's value
 * @template I - The insertion semantics ('required' | 'optional' | 'withDefault' | 'generated')
 */
export class Column<
  T,
  I extends InsertionType = 'optional',
  GeneratedColumns extends object = Record<string, SqlFragment>,
> implements SqlExpression<T> {
  declare readonly [sqlExpressionResultType]: (value: T) => T

  private _tableName: string = ''
  private _tableSchema: string | undefined
  private _tableDialect: Dialect = 'postgres'
  private _columnName: string = ''
  private _dataType: string
  private _isNullable: boolean
  private _isPrimaryKey: boolean = false
  private _isUnique: boolean = false
  private _defaultValue?: string
  private _references?: ColumnMetadata['references']
  // Lazy reference callback - resolved during table binding
  private _lazyReference?: LazyReference
  private _zodSchema: ZodType<T>
  private _inputSchemaCache?: ZodType<T>
  private _outputSchemaCache?: ZodType<T>
  private _insertionType: I
  private _encoder?: (value: T) => unknown
  private _decoder?: (value: unknown) => T
  private _valueSql?: ValueSql
  private _generated?: {
    expression?: (columns: Record<string, SqlFragment>) => SqlFragment
    stored: boolean
  }
  private _enumValues?: readonly string[]
  private _enumName?: string
  private _comment?: string
  private _defaultFn?: () => T
  private _identity?: 'generatedAlways' | 'identity'
  private _constraints: ColumnMetadata['constraints'] = []

  constructor(
    dataType: string,
    zodSchema: ZodType<T>,
    insertionType: I,
    isNullable: boolean = true,
    encoder?: (value: T) => unknown,
    decoder?: (value: unknown) => T,
    enumValues?: readonly string[],
    enumName?: string,
    valueSql?: ValueSql,
    generated?: {
      expression?: (columns: Record<string, SqlFragment>) => SqlFragment
      stored: boolean
    },
  ) {
    this._dataType = dataType
    this._zodSchema = zodSchema
    this._insertionType = insertionType
    this._isNullable = isNullable
    this._encoder = encoder
    this._decoder = decoder
    this._enumValues = enumValues
    this._enumName = enumName
    this._valueSql = valueSql
    this._generated = generated
  }

  /**
   * Sets the column name early (before table binding) so toString() works in view query closures.
   */
  _setColumnName(name: string): void {
    if (!this._columnName) this._columnName = name
  }

  /**
   * Returns the quoted column name for use in view query template literals.
   */
  toString(): string {
    return `"${this._columnName}"`
  }

  /**
   * Called internally when the column is added to a table (phase 1 of binding).
   * Sets table and column names but does NOT resolve lazy references yet.
   * @param schema - Logical schema; rendered per target dialect.
   * @param dialect - Table's authored dialect.
   */
  _bind(
    tableName: string,
    columnName: string,
    schema?: string,
    dialect: Dialect = 'postgres',
  ): void {
    this._tableName = tableName
    this._tableSchema = schema
    this._tableDialect = dialect
    this._columnName = columnName

    // For enum columns, set the enum type name based on column name
    if (this._enumValues && !this._enumName) {
      this._enumName = `${columnName}_enum`
      this._dataType = `"${this._enumName}"`
    }
  }

  /**
   * Called after all tables are bound to resolve lazy foreign key references (phase 2).
   * At this point, all referenced columns should have their table/column names set.
   */
  _resolveLazyReferences(): void {
    if (this._lazyReference) {
      const targetCol = this._lazyReference.ref()
      this._references = {
        table: targetCol._tableName,
        schema: targetCol._tableSchema,
        column: targetCol._columnName,
        onDelete: this._lazyReference.onDelete,
        onUpdate: this._lazyReference.onUpdate,
      }
      this._lazyReference = undefined
    }
  }

  /**
   * Creates a clone of this column with modifications
   */
  private _clone<
    NewT = T,
    NewI extends InsertionType = I,
    NewGeneratedColumns extends object = GeneratedColumns,
  >(
    overrides: Partial<{
      dataType: string
      zodSchema: ZodType<NewT>
      insertionType: NewI
      isNullable: boolean
      isPrimaryKey: boolean
      isUnique: boolean
      defaultValue: string | undefined
      references: ColumnMetadata['references']
      lazyReference: LazyReference
      encoder: (value: NewT) => unknown
      decoder: (value: unknown) => NewT
      valueSql: ValueSql
      generated: {
        expression?: (columns: Record<string, SqlFragment>) => SqlFragment
        stored: boolean
      }
      enumValues: readonly string[]
      enumName: string
      comment: string
      defaultFn: () => NewT
      identity: 'generatedAlways' | 'identity'
      constraints: ColumnMetadata['constraints']
    }> = {},
  ): Column<NewT, NewI, NewGeneratedColumns> {
    const ColumnClass = this.constructor as new (
      dataType: string,
      zodSchema: ZodType<NewT>,
      insertionType: NewI,
      isNullable: boolean,
      encoder?: (value: NewT) => unknown,
      decoder?: (value: unknown) => NewT,
      enumValues?: readonly string[],
      enumName?: string,
      valueSql?: ValueSql,
      generated?: {
        expression?: (columns: Record<string, SqlFragment>) => SqlFragment
        stored: boolean
      },
    ) => Column<NewT, NewI, NewGeneratedColumns>
    const col = new ColumnClass(
      overrides.dataType ?? this._dataType,
      (overrides.zodSchema ?? this._zodSchema) as ZodType<NewT>,
      (overrides.insertionType ?? this._insertionType) as NewI,
      overrides.isNullable ?? this._isNullable,
      overrides.encoder ??
        (this._encoder as unknown as (value: NewT) => unknown),
      overrides.decoder ??
        (this._decoder as unknown as (value: unknown) => NewT),
      overrides.enumValues ?? this._enumValues,
      overrides.enumName ?? this._enumName,
      overrides.valueSql ?? this._valueSql,
      overrides.generated ?? this._generated,
    )
    col._tableName = this._tableName
    col._columnName = this._columnName
    col._isPrimaryKey = overrides.isPrimaryKey ?? this._isPrimaryKey
    col._isUnique = overrides.isUnique ?? this._isUnique
    col._defaultValue = overrides.defaultValue ?? this._defaultValue
    col._references = overrides.references ?? this._references
    col._lazyReference = overrides.lazyReference ?? this._lazyReference
    col._comment = overrides.comment ?? this._comment
    col._defaultFn =
      overrides.defaultFn ?? (this._defaultFn as unknown as () => NewT)
    col._identity = overrides.identity ?? this._identity
    col._constraints = overrides.constraints ?? this._constraints
    return col
  }

  // ==================== Modifiers ====================

  /**
   * Makes the column NOT NULL, changing its insertion type to 'required'
   */
  notNull(): Column<
    NonNullable<T>,
    I extends 'optional' ? 'required' : I,
    GeneratedColumns
  > {
    return this._clone({
      zodSchema: this._zodSchema as unknown as ZodType<NonNullable<T>>,
      insertionType: (this._insertionType === 'optional'
        ? 'required'
        : this._insertionType) as I extends 'optional' ? 'required' : I,
      isNullable: false,
    })
  }

  /**
   * Adds a UNIQUE constraint to the column
   */
  unique(): Column<T, I, GeneratedColumns> {
    return this._clone({ isUnique: true })
  }

  /**
   * Sets a DEFAULT value. Accepts an app-side value of the column's TypeScript
   * type (T) or a raw SQL fragment via sql`expr`.
   *
   * The value is run through the column's encoder (if any) before being
   * serialized into the DDL, so app types map to their stored DB types:
   * - `c.enum({ admin: 1, user: 2 }).default('admin')` → `DEFAULT 1` (sqlite code)
   * - `c.boolean().default(true)` → `DEFAULT 1` (sqlite)
   * - `c.datetime().default(new Date(0))` → `DEFAULT 0` (sqlite ms)
   * - `c.text().default('active')` → `DEFAULT 'active'`
   * - `c.timestamptz().default(sql`NOW()`)` → `DEFAULT NOW()`
   */
  default(
    value: T | SqlFragment,
  ): Column<T, I extends 'required' ? 'withDefault' : I, GeneratedColumns> {
    let sqlValue: string
    if (isSqlFragment(value)) {
      sqlValue = value.toSQL(0).sql
    } else {
      const encoded = this._encoder ? this._encoder(value) : value
      sqlValue = serializeDefaultLiteral(encoded)
    }
    return this._clone({
      defaultValue: sqlValue,
      insertionType: (this._insertionType === 'required'
        ? 'withDefault'
        : this._insertionType) as I extends 'required' ? 'withDefault' : I,
    })
  }

  /**
   * Sets a JS-side default value generator. Invoked at insert time when the
   * column is omitted from the row. Unlike `default()`, this does not emit
   * any SQL DEFAULT clause; the value is computed in the application layer.
   *
   * - Promotes 'required' insertion type to 'withDefault' (mirrors `default()`)
   * - Other insertion types are preserved
   */
  $defaultFn(
    fn: () => T,
  ): Column<T, I extends 'required' ? 'withDefault' : I, GeneratedColumns> {
    return this._clone({
      defaultFn: fn,
      insertionType: (this._insertionType === 'required'
        ? 'withDefault'
        : this._insertionType) as I extends 'required' ? 'withDefault' : I,
    })
  }

  /**
   * Makes this column the PRIMARY KEY
   * - For UUID columns, automatically adds DEFAULT gen_random_uuid()
   * - For SERIAL columns, keeps "generated" insertion type
   */
  primaryKey(): Column<
    NonNullable<T>,
    I extends 'generated' ? 'generated' : 'withDefault',
    GeneratedColumns
  > {
    const isUuid = this._dataType === 'UUID'
    const isGenerated = this._insertionType === 'generated'
    return this._clone({
      zodSchema: this._zodSchema as unknown as ZodType<NonNullable<T>>,
      insertionType: (isGenerated
        ? 'generated'
        : 'withDefault') as I extends 'generated' ? 'generated' : 'withDefault',
      isPrimaryKey: true,
      isNullable: false,
      defaultValue: isUuid ? 'gen_random_uuid()' : this._defaultValue,
    })
  }

  /**
   * Adds a FOREIGN KEY reference to another column.
   * The reference is resolved lazily during table binding to support
   * references to unbound tables.
   */
  references(
    // oxlint-disable-next-line typescript/no-explicit-any
    ref: () => Column<any, any, any>,
    options?: {
      onDelete?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
      onUpdate?: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'
    },
  ): Column<T, I, GeneratedColumns> {
    // Store the reference callback for lazy resolution during binding
    return this._clone({
      lazyReference: {
        ref,
        onDelete: options?.onDelete,
        onUpdate: options?.onUpdate,
      },
    })
  }

  /**
   * Overrides the column's TypeScript type without changing its SQL type.
   * Optionally accepts a Zod schema so the override also propagates to
   * generated select/insert/view schemas.
   */
  $type<NewT>(): Column<NewT, I, GeneratedColumns>
  $type<NewT>(schema: ZodType<NewT>): Column<NewT, I, GeneratedColumns>
  $type<NewT>(schema?: ZodType<NewT>): Column<NewT, I, GeneratedColumns> {
    return this._clone<NewT>({
      zodSchema: (schema ?? this._zodSchema) as ZodType<NewT>,
    })
  }

  /**
   * Attaches a human-readable comment to this column. No effect on DDL or runtime behaviour.
   */
  comment(_text: string): Column<T, I, GeneratedColumns> {
    return this._clone({ comment: _text })
  }

  constrain(
    name: string,
    expression: (column: SqlFragment) => SqlFragment,
  ): Column<T, I, GeneratedColumns> {
    return this._clone({
      constraints: [...this._constraints, { name, expression }],
    })
  }

  _markGenerated(): Column<T, 'generated', GeneratedColumnMarker> {
    return this._clone<T, 'generated', GeneratedColumnMarker>({
      insertionType: 'generated',
      generated: { stored: false },
    })
  }

  _markIdentity(
    identity: 'generatedAlways' | 'identity',
  ): Column<T, 'generated', GeneratedColumns> {
    return this._clone<T, 'generated'>({
      insertionType: 'generated',
      identity,
      isNullable: false,
    })
  }

  _withGeneratedExpression(
    expression: (columns: Record<string, SqlFragment>) => SqlFragment,
  ): Column<T, I, GeneratedColumns> {
    return this._clone({
      generated: {
        ...(this._generated ?? { stored: false }),
        expression,
      },
    })
  }

  as(
    expression: (columns: GeneratedColumns) => SqlFragment,
  ): Column<T, I, GeneratedColumns> {
    return this._withGeneratedExpression(
      expression as (columns: Record<string, SqlFragment>) => SqlFragment,
    )
  }

  get isGeneratedColumn(): boolean {
    return this._generated !== undefined
  }

  stored(): Column<T, I, GeneratedColumns> {
    return this._clone({
      generated: {
        ...this._generated,
        stored: true,
      },
    })
  }

  // ==================== Filter Operators ====================

  /**
   * col = value or col = other_col
   */
  eq(value: T | Column<T> | ColumnRef<T>): Condition {
    if (isColumnRef(value)) {
      return this.columnCondition('=', value)
    }
    const encoded = this.encode(value)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      '=',
      encoded,
    )
  }

  /**
   * col != value or col != other_col
   */
  ne(value: T | Column<T> | ColumnRef<T>): Condition {
    if (isColumnRef(value)) {
      return this.columnCondition('!=', value)
    }
    const encoded = this.encode(value)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      '!=',
      encoded,
    )
  }

  /**
   * col > value or col > other_col
   */
  gt(value: T | Column<T> | ColumnRef<T>): Condition {
    if (isColumnRef(value)) {
      return this.columnCondition('>', value)
    }
    const encoded = this.encode(value)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      '>',
      encoded,
    )
  }

  /**
   * col >= value or col >= other_col
   */
  gte(value: T | Column<T> | ColumnRef<T>): Condition {
    if (isColumnRef(value)) {
      return this.columnCondition('>=', value)
    }
    const encoded = this.encode(value)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      '>=',
      encoded,
    )
  }

  /**
   * col < value or col < other_col
   */
  lt(value: T | Column<T> | ColumnRef<T>): Condition {
    if (isColumnRef(value)) {
      return this.columnCondition('<', value)
    }
    const encoded = this.encode(value)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      '<',
      encoded,
    )
  }

  /**
   * col <= value or col <= other_col
   */
  lte(value: T | Column<T> | ColumnRef<T>): Condition {
    if (isColumnRef(value)) {
      return this.columnCondition('<=', value)
    }
    const encoded = this.encode(value)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      '<=',
      encoded,
    )
  }

  /**
   * col LIKE pattern
   */
  like(pattern: string): Condition {
    const encoded = this.encode(pattern as T)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      'LIKE',
      encoded,
    )
  }

  /**
   * col ILIKE pattern (case-insensitive)
   */
  ilike(pattern: string): Condition {
    const encoded = this.encode(pattern as T)
    return this.valueCondition(
      (dialect) => this.qualifiedNameFor(dialect),
      'ILIKE',
      encoded,
    )
  }

  /**
   * col IN (value1, value2, ...)
   */
  inArray(values: T[]): Condition {
    if (values.length === 0) {
      return Condition.false()
    }
    const encoded = values.map((v) => this.encode(v))
    return new Condition({
      toSQL: (paramOffset: number, dialect: Dialect = this._tableDialect) => {
        const params: ParamWithMeta[] = []
        const placeholders: string[] = []
        let offset = paramOffset
        for (const value of encoded) {
          const rendered = this.valueSql(value, offset, dialect)
          placeholders.push(rendered.sql)
          params.push(...rendered.params)
          offset += rendered.params.length
        }
        return {
          sql: `${this.qualifiedNameFor(dialect)} IN (${placeholders.join(', ')})`,
          params,
        }
      },
    })
  }

  /**
   * col IS NULL
   */
  isNull(): Condition {
    return this.suffixCondition('IS NULL')
  }

  /**
   * col IS NOT NULL
   */
  isNotNull(): Condition {
    return this.suffixCondition('IS NOT NULL')
  }

  /**
   * col BETWEEN a AND b
   */
  between(a: T, b: T): Condition {
    const encodedA = this.encode(a)
    const encodedB = this.encode(b)
    return new Condition({
      toSQL: (paramOffset: number, dialect: Dialect = this._tableDialect) => {
        const aSql = this.valueSql(encodedA, paramOffset, dialect)
        const bSql = this.valueSql(
          encodedB,
          paramOffset + aSql.params.length,
          dialect,
        )
        return {
          sql: `${this.qualifiedNameFor(dialect)} BETWEEN ${aSql.sql} AND ${bSql.sql}`,
          params: [...aSql.params, ...bSql.params],
        }
      },
    })
  }

  add(value: NumericOperand<T>): NumericResult<T> {
    return this._numericExpression('+', value)
  }

  sub(value: NumericOperand<T>): NumericResult<T> {
    return this._numericExpression('-', value)
  }

  mul(value: NumericOperand<T>): NumericResult<T> {
    return this._numericExpression('*', value)
  }

  div(value: NumericOperand<T>): NumericResult<T> {
    return this._numericExpression('/', value)
  }

  mod(value: NumericOperand<T>): NumericResult<T> {
    return this._numericExpression('%', value)
  }

  private _numericExpression(
    operator: '+' | '-' | '*' | '/' | '%',
    value: NumericOperand<T>,
  ): NumericResult<T> {
    const expression = new NumericSqlExpression<NumericValue>(
      this,
      operator,
      value as NumericValue | SqlExpression<NumericValue>,
      (operand, paramOffset, dialect) => {
        const encoded = this.encode(operand as T)
        return this.valueSql(encoded, paramOffset, dialect)
      },
    )
    return expression as unknown as NumericResult<T>
  }

  equalityCheck(value: T): SecurityCheckContext {
    if (!this._tableName) {
      throw new Error('Column must be attached to a table to use equalityCheck')
    }

    return {
      tableName: this._tableName,
      columnName: this._columnName,
      value,
      operator: '=',
    }
  }

  assertImmutable(data?: Record<string, unknown>, allowedValue?: T): void {
    if (!data) return
    if (
      this._columnName in data &&
      data[this._columnName] !== undefined &&
      data[this._columnName] !== allowedValue
    ) {
      throw new Error(`Column ${this._columnName} is immutable`)
    }
  }

  // ==================== Getters ====================

  /**
   * Gets the fully qualified column name: "table"."column"
   */
  get qualifiedName(): string {
    return this.qualifiedNameFor(this._tableDialect)
  }

  private qualifiedNameFor(dialect: Dialect): string {
    if (!this._tableName) return `"${this._columnName}"`
    return `${tableSqlRef(this._tableName, this._tableSchema, dialect)}."${this._columnName}"`
  }

  /**
   * Gets just the column name (quoted)
   */
  get name(): ColumnName {
    return this._columnName as ColumnName
  }

  /**
   * Gets the Zod schema for this column's type, including decoder transform
   */
  get zodType(): ZodType<T> {
    if (this._outputSchemaCache) return this._outputSchemaCache
    // Build a schema that:
    // 1. Accepts any input (preprocess to handle raw DB values)
    // 2. Applies the decoder to transform the value
    // 3. Validates with the original schema
    let schema: ZodType

    if (this._decoder) {
      // Use preprocess to decode before validation
      schema = z.preprocess((val) => {
        if (val === null) return null
        return safe(this._decoder)(val)
      }, this._zodSchema)
    } else {
      schema = this._zodSchema
    }

    if (this._isNullable) {
      this._outputSchemaCache = schema.nullable() as unknown as ZodType<T>
    } else {
      this._outputSchemaCache = schema as ZodType<T>
    }
    return this._outputSchemaCache
  }

  /**
   * Gets the schema for application values, without output decoder preprocessing.
   */
  get inputZodType(): ZodType<T> {
    this._inputSchemaCache ??= this._isNullable
      ? this._zodSchema.nullable() as unknown as ZodType<T>
      : this._zodSchema
    return this._inputSchemaCache
  }

  /**
   * Gets the insertion type for this column
   */
  get insertionType(): I {
    return this._insertionType
  }

  /**
   * Gets metadata for DDL generation
   */
  get metadata(): ColumnMetadata {
    return {
      name: this._columnName,
      dataType: this._dataType,
      isNullable: this._isNullable,
      isPrimaryKey: this._isPrimaryKey,
      isUnique: this._isUnique,
      defaultValue: this._defaultValue,
      references: this._references,
      zodSchema: this._zodSchema,
      enumValues: this._enumValues,
      enumName: this._enumName,
      identity: this._identity,
      generated: this._generated?.expression
        ? {
            expression: this._generated.expression,
            stored: this._generated.stored,
          }
        : undefined,
      constraints: this._constraints,
    }
  }

  /**
   * Whether a JS-side default function is set via `$defaultFn`.
   */
  get hasDefaultFn(): boolean {
    return this._defaultFn !== undefined
  }

  /**
   * Invokes the JS-side default function. Caller must check `hasDefaultFn`.
   */
  applyDefaultFn(): T {
    if (!this._defaultFn) {
      throw new Error(`Column "${this._columnName}" has no $defaultFn`)
    }
    return this._defaultFn()
  }

  /**
   * Validates and encodes an application value for a column-aware SQL helper.
   */
  encode(value: T): unknown {
    const result = this.inputZodType.safeParse(value)
    if (!result.success) {
      const columnPath = [this._tableName, this._columnName]
        .filter(Boolean)
        .join('.')
      const issues = result.error.issues
        .map((issue) => {
          const issuePath = issue.path?.length
            ? `${columnPath}.${issue.path.map(String).join('.')}`
            : columnPath
          return `${issuePath}: ${issue.message} [${issue.code}]`
        })
        .join('; ')
      throw new Error(
        `Invalid application value for ${columnPath} (database type ${this._dataType}). ${issues}. Rejected value: ${formatRejectedValue(value, this._columnName)}`,
      )
    }

    const validated = result.data
    if (validated === null) return null
    return this._encoder ? this._encoder(validated) : validated
  }

  valueSql(
    value: unknown,
    paramOffset: number,
    dialect: Dialect = 'postgres',
  ): { sql: string; params: ParamWithMeta[] } {
    if (value === null || !this._valueSql) {
      return {
        sql:
          dialect === 'sqlite' || dialect === 'snowflake'
            ? '?'
            : `$${paramOffset}`,
        params: [{ value, columnName: this._columnName }],
      }
    }

    const fragment = this._valueSql(value)
    const rendered = renderFragment(fragment, paramOffset, dialect)
    return {
      sql: rendered.sql,
      params: rendered.params.map((param) => ({
        value: param.value,
        columnName: param.columnName ?? this._columnName,
      })),
    }
  }

  valueCondition(
    left: string | ((dialect: Dialect) => string),
    operator: string,
    encoded: unknown,
  ): Condition {
    return new Condition({
      toSQL: (paramOffset: number, dialect: Dialect = this._tableDialect) => {
        const rendered = this.valueSql(encoded, paramOffset, dialect)
        return {
          sql: `${typeof left === 'function' ? left(dialect) : left} ${operator} ${rendered.sql}`,
          params: rendered.params,
        }
      },
    })
  }

  private columnCondition(operator: string, right: ColumnRef<T>): Condition {
    return new Condition({
      toSQL: (_paramOffset: number, dialect: Dialect = this._tableDialect) => ({
        sql: `${this.qualifiedNameFor(dialect)} ${operator} ${renderColumnRef(right, dialect)}`,
        params: [],
      }),
    })
  }

  private suffixCondition(suffix: string): Condition {
    return new Condition({
      toSQL: (_paramOffset: number, dialect: Dialect = this._tableDialect) => ({
        sql: `${this.qualifiedNameFor(dialect)} ${suffix}`,
        params: [],
      }),
    })
  }

  /**
   * Decodes a value from the database
   */
  decode(value: unknown): T {
    if (this._decoder) {
      return this._decoder(value)
    }
    return value as T
  }

  // ==================== SqlFragment Implementation ====================

  toSQL(
    _paramOffset: number,
    dialect: Dialect = this._tableDialect,
  ): { sql: string; params: ParamWithMeta[] } {
    return { sql: this.qualifiedNameFor(dialect), params: [] }
  }

  /**
   * Returns a SqlFragment that outputs just the column name (unqualified).
   * Useful for ON CONFLICT, ORDER BY, etc. where table qualification isn't wanted.
   */
  get col(): SqlFragment {
    const colName = this._columnName
    return {
      toSQL(_paramOffset: number): { sql: string; params: ParamWithMeta[] } {
        return { sql: `"${colName}"`, params: [] }
      },
    }
  }

  /**
   * Returns a SqlFragment that outputs EXCLUDED."column" for use in ON CONFLICT DO UPDATE.
   */
  get excluded(): SqlFragment {
    const colName = this._columnName
    return {
      toSQL(_paramOffset: number): { sql: string; params: ParamWithMeta[] } {
        return { sql: `EXCLUDED."${colName}"`, params: [] }
      },
    }
  }
}

function renderFragment(
  fragment: SqlFragment,
  paramOffset: number,
  dialect: Dialect,
): { sql: string; params: ParamWithMeta[] } {
  if (
    'toSQLWithOffset' in fragment &&
    typeof fragment.toSQLWithOffset === 'function'
  ) {
    const withOffset = fragment as SqlFragment & {
      toSQLWithOffset: (
        paramOffset: number,
        dialect: Dialect,
      ) => { sql: string; params: ParamWithMeta[] }
    }
    return withOffset.toSQLWithOffset(paramOffset, dialect)
  }
  return fragment.toSQL(paramOffset, dialect)
}
